import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export default class EventClassgroupController extends Controller {
  @service eventData;
  @service resultsCalculator;
  @service preferences;

  // Query params for element view mode
  queryParams = ['element'];
  @tracked element = null; // Course element UUID for focused view

  // This computed getter will automatically re-run when:
  // 1. The model changes
  // 2. Any TrackedMap accessed inside it changes (entries, startRecords, finishRecords, etc.)
  get results() {
    const { eventUuid, classGroupUuid } = this.model;
    const showPredictions = this.preferences.isColumnVisible(classGroupUuid, 'showPredictions');
    const baseResults = this.resultsCalculator.calculateResultsForClassGroup(eventUuid, classGroupUuid, { showPredictions });

    // In element mode, recalculate places based on elapsed time at the focused element
    if (this.viewMode === 'element' && this.element) {
      return this.recalculatePlacesForElement(baseResults, this.element);
    }

    return baseResults;
  }

  /**
   * Recalculate place rankings based on elapsed time at a specific course element
   */
  recalculatePlacesForElement(results, courseElementUuid) {
    // Get elapsed time at the element for each result
    const resultsWithElapsed = results.map(result => {
      const splitData = result.splitsMap?.[courseElementUuid];
      return {
        ...result,
        elementElapsed: splitData?.elapsed ?? null
      };
    });

    // Sort by elapsed time (null values last)
    const sorted = [...resultsWithElapsed].sort((a, b) => {
      if (a.elementElapsed === null && b.elementElapsed === null) return 0;
      if (a.elementElapsed === null) return 1;
      if (b.elementElapsed === null) return -1;
      return a.elementElapsed - b.elementElapsed;
    });

    // Assign new places based on element elapsed time
    let currentPlace = 1;
    let lastElapsed = null;
    let sameTimeCount = 0;

    return sorted.map((result, index) => {
      let place = null;

      if (result.elementElapsed !== null) {
        if (lastElapsed !== null && result.elementElapsed === lastElapsed) {
          // Same time as previous - same place
          sameTimeCount++;
        } else {
          // Different time - new place (accounting for ties)
          currentPlace = index + 1;
          sameTimeCount = 0;
        }
        place = currentPlace;
        lastElapsed = result.elementElapsed;
      }

      return {
        ...result,
        place // Override the original place with element-based place
      };
    });
  }

  get classGroup() {
    const { eventUuid, classGroupUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    // classGroups map is keyed by uuid
    return eventDataObj?.classGroups.get(classGroupUuid);
  }

  /**
   * Get the course for this classgroup to determine split columns
   */
  get course() {
    const { eventUuid, classGroupUuid } = this.model;
    return this.eventData.getCourseForClassGroup(eventUuid, classGroupUuid);
  }

  /**
   * Get expected timing points (public splits only) for column headers
   * Uses course element UUID as the key (not timing point UUID) because
   * multiple course elements can reference the same timing point in lap races
   */
  get splitColumns() {
    const course = this.course;
    if (!course) return [];

    const courseElements = course.courseElements || course.CourseElements || [];
    return courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null && ce.isPublic)
      .sort((a, b) => a.order - b.order)
      .map(ce => ({
        name: ce.name || ce.intermediateTimingPointName,
        uuid: ce.uuid  // Use course element UUID, not timing point UUID
      }));
  }

  /**
   * Check if we have any splits to display
   */
  get hasSplits() {
    return this.splitColumns.length > 0;
  }

  /**
   * Get event precision for time formatting
   */
  get precision() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.event?.precision || 1000;
  }

  /**
   * Format a time in milliseconds to display format
   */
  formatSplitTime(milliseconds) {
    if (milliseconds === null || milliseconds === undefined) {
      return '—';
    }

    const precision = this.precision;
    const isNegative = milliseconds < 0;
    const absMilliseconds = Math.abs(milliseconds);

    const base = dayjs.utc(absMilliseconds).format('HH:mm:ss');
    const ms = absMilliseconds % 1000;

    let formatted;
    switch (precision) {
      case 1000:
        formatted = base;
        break;
      case 100:
        formatted = `${base}.${Math.floor(ms / 100)}`;
        break;
      case 10:
        formatted = `${base}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
        break;
      case 1:
        formatted = `${base}.${String(ms).padStart(3, '0')}`;
        break;
      default:
        formatted = base;
    }

    return isNegative ? `-${formatted}` : formatted;
  }

  /**
   * Get split time for a result at a specific course element
   */
  getSplitForResult(result, courseElementUuid) {
    if (!result.splits) return null;
    const split = result.splits.find(s => s.courseElementUuid === courseElementUuid);
    return split?.split;
  }

  /**
   * Get elapsed time for a result at a specific course element
   */
  getElapsedForResult(result, courseElementUuid) {
    if (!result.splits) return null;
    const split = result.splits.find(s => s.courseElementUuid === courseElementUuid);
    return split?.elapsed;
  }

  get snapshotComplete() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.snapshotComplete || false;
  }

  get lastUpdated() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.lastUpdated;
  }

  get eventName() {
    const { eventUuid } = this.model;
    const eventDataObj = this.eventData.getEventData(eventUuid);
    return eventDataObj?.event?.name || 'Loading...';
  }

  /**
   * View mode: 'overall' or 'element'
   */
  get viewMode() {
    return this.element ? 'element' : 'overall';
  }

  /**
   * Get the focused course element details (when in element view mode)
   */
  get focusedElement() {
    if (!this.element) return null;

    const course = this.course;
    if (!course) return null;

    const courseElements = course.courseElements || course.CourseElements || [];
    return courseElements.find(ce => ce.uuid === this.element);
  }

  /**
   * Get split columns for the focused element only (for element view mode)
   */
  get focusedSplitColumns() {
    const focused = this.focusedElement;
    if (!focused) return [];

    return [{
      name: focused.name || focused.intermediateTimingPointName,
      uuid: focused.uuid
    }];
  }

  /**
   * Page title based on view mode
   */
  get pageTitle() {
    if (this.viewMode === 'element' && this.focusedElement) {
      return `${this.classGroup?.name} - ${this.focusedElement.name || this.focusedElement.intermediateTimingPointName}`;
    }
    return this.classGroup?.name || 'Results';
  }
}
